// apps/web/src/pages/cadastros/materiais-mass-edit-page.tsx — Fase 03, Plano 03 (CAD-02, OPS-08).
import React, { useEffect, useState, useRef } from 'react';
import { FormDialog, FormDialogContent, FormDialogHeader, FormDialogTitle, FormDialogBody, FormDialogFooter } from '../../components/ui/form-dialog';
import {
  Filter,
  CheckSquare,
  Square,
  Edit3,
  Plus,
  AlertOctagon,
  CheckCircle2,
  RefreshCw,
  Package,
} from 'lucide-react';
import type {
  MaterialEntity,
  MaterialListResponse,
  MaterialMassEditPayload,
  TipoBanho,
  TipoTinta,
  FamiliaCor,
  PortePeca,
  ParametrosEmMassaInput,
  ParametrosEmMassaResultado,
} from '@pieltec/contracts';
import { apiMutate } from '../../lib/csrf-fetch';
import { LocalizacaoSelect } from '../../components/estoque/localizacao-select';
import { lerFatorDigitado, linhasResumoEmMassa, mensagemErroParametros } from '../../lib/estoque/parametros';
import { obterSessaoAtual } from '../../lib/session';
import { lerErroApi } from '../../lib/vendas/erro-api';
import { MaterialFormModal } from './materiais-form';
import { AlertTriangle } from 'lucide-react';
import { camposProcessoPendentes, rotuloBanho, rotuloTinta, opcoesFamiliaCor, opcoesPorte, opcoesBanho, opcoesTinta } from '../../lib/material-atributos';
import { rotuloKindMaterial, rotuloStatus } from '../../lib/rotulos';
import { TablePagination } from '../../components/ui/table-pagination';
import { CampoSelect } from '../../components/ui/campo-select';

/** Estado de partida (e de reinício após aplicar) do bloco "Estoque e compras": nenhum campo marcado. */
const ESTOQUE_EDIT_INICIAL = {
  unidadeCompra: { aplicar: false, valor: '' },
  fatorConversao: { aplicar: false, valor: '' },
  estoquePreferencialId: { aplicar: false, valor: '' },
  altoGiroSemDevolucao: { aplicar: false, valor: false },
};

function umDos<T extends string>(valor: string, opcoes: readonly T[]): T | null {
  return (opcoes as readonly string[]).includes(valor) ? valor as T : null;
}

function ehMaterialListResponse(valor: unknown): valor is MaterialListResponse {
  if (typeof valor !== 'object' || valor === null) return false;
  const dados = valor as Record<string, unknown>;
  return Array.isArray(dados.data) && typeof dados.hasMore === 'boolean' && (typeof dados.nextCursor === 'string' || dados.nextCursor === null);
}

function ehResultadoEmMassa(valor: unknown): valor is ParametrosEmMassaResultado {
  if (typeof valor !== 'object' || valor === null) return false;
  const dados = valor as Record<string, unknown>;
  return typeof dados.totalAlterados === 'number' && typeof dados.totalInalterados === 'number';
}

export const MateriaisMassEditPage: React.FC = () => {
  const [materiais, setMateriais] = useState<MaterialEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [pageCursors, setPageCursors] = useState<Array<string | null>>([null]);

  // Filtros
  const [buscaFamily, setBuscaFamily] = useState('');
  const [busca, setBusca] = useState(() => typeof window === 'undefined' ? '' : new URLSearchParams(window.location.search).get('busca') ?? '');
  const [buscaStatus, setBuscaStatus] = useState('');
  const [buscaKind, setBuscaKind] = useState('');

  // Seleção
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // WR-08 (03-REVIEW.md): MaterialFormModal existia mas nunca era aberto por nenhuma tela —
  // `undefined` = fechado, `null` = criando material novo, entidade = editando material existente.
  const [materialEditando, setMaterialEditando] = useState<MaterialEntity | null | undefined>(undefined);

  // Modal de edição em massa
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [feedback, setFeedback] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);

  // Bloco "Estoque e compras" (Fase 13, D-10/D-03/D-21): só com estoque:configurar; botão e confirmação próprios.
  const [podeConfigurarEstoque, setPodeConfigurarEstoque] = useState(false);
  const [estoqueConfirmOpen, setEstoqueConfirmOpen] = useState(false);
  const [estoqueErro, setEstoqueErro] = useState<string | null>(null);
  const [estoqueAplicando, setEstoqueAplicando] = useState(false);
  const [estoqueEdit, setEstoqueEdit] = useState(ESTOQUE_EDIT_INICIAL);
  const [nomesLocaisEstoque, setNomesLocaisEstoque] = useState<Readonly<Record<string, string>>>({});

  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (vivo && sessao.autenticado) setPodeConfigurarEstoque(sessao.usuario.permissions.includes('estoque:configurar'));
    });
    return () => {
      vivo = false;
    };
  }, []);

  // Estado dos campos a editar
  const [camposEdit, setCamposEdit] = useState<{
    family: { aplicar: boolean; valor: string };
    status: { aplicar: boolean; valor: string };
    kind: { aplicar: boolean; valor: string };
    stage: { aplicar: boolean; valor: string };
    unit: { aplicar: boolean; valor: string };
    cellCount: { aplicar: boolean; valor: number };
    description: { aplicar: boolean; valor: string };
    pinta: { aplicar: boolean; valor: boolean };
    tipoTinta: { aplicar: boolean; valor: string };
    corCodigo: { aplicar: boolean; valor: string };
    corFamilia: { aplicar: boolean; valor: string };
    porte: { aplicar: boolean; valor: string };
    exigeIsolamento: { aplicar: boolean; valor: string };
    tipoBanho: { aplicar: boolean; valor: string };
    areaUnitariaM2: { aplicar: boolean; valor: string };
    pecasPorGancheira: { aplicar: boolean; valor: string };
  }>({
    family: { aplicar: false, valor: '' },
    status: { aplicar: false, valor: 'ACTIVE' },
    kind: { aplicar: false, valor: 'PRODUTO' },
    stage: { aplicar: false, valor: '' },
    unit: { aplicar: false, valor: 'un' },
    cellCount: { aplicar: false, valor: 1 },
    description: { aplicar: false, valor: '' },
    pinta: { aplicar: false, valor: false },
    tipoTinta: { aplicar: false, valor: '' },
    corCodigo: { aplicar: false, valor: '' },
    corFamilia: { aplicar: false, valor: '' },
    porte: { aplicar: false, valor: '' },
    exigeIsolamento: { aplicar: false, valor: '' },
    tipoBanho: { aplicar: false, valor: '' },
    areaUnitariaM2: { aplicar: false, valor: '' },
    pecasPorGancheira: { aplicar: false, valor: '' },
  });

  const requestVersion = useRef(0);
  const carregarPagina = async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    setFeedback(null);
    try {
      const params = new URLSearchParams({
        limit: String(pageSize),
        family: buscaFamily,
        busca,
        status: buscaStatus,
        kind: buscaKind,
      });
      const cursor = pageCursors[page - 1];
      if (cursor) params.set('cursor', cursor);

      const res = await fetch(`/api/v1/cadastros/materiais?${params.toString()}`);
      if (!res.ok) throw new Error('Consulta indisponível');
      if (res.ok) {
        const json: unknown = await res.json();
        if (!ehMaterialListResponse(json)) throw new Error('Resposta de materiais inválida.');
        const data = json;
        if (version !== requestVersion.current) return;
        setMateriais([...data.data]);
        setNextCursor(data.nextCursor);
        setHasMore(data.hasMore);
        setTotal(data.total ?? data.data.length);
        if (data.nextCursor) {
          setPageCursors((current) => {
            const next = current.slice(0, page);
            next[page] = data.nextCursor;
            return next;
          });
        }
      }
    } catch {
      if (version === requestVersion.current) { setMateriais([]); setTotal(0); setFeedback({ tipo: 'erro', texto: 'Não foi possível consultar os materiais. Tente atualizar a lista.' }); }
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  };

  useEffect(() => {
    ++requestVersion.current;
    const timer = window.setTimeout(() => void carregarPagina(), 250);
    setSelectedIds(new Set());
    return () => { window.clearTimeout(timer); ++requestVersion.current; };
  }, [page, pageSize, buscaFamily, buscaStatus, buscaKind, busca]);

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === materiais.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(materiais.map((m) => m.id)));
    }
  };

  /** Monta o corpo da edição em massa de parâmetros de estoque; devolve a mensagem de erro quando não dá. */
  const montarParametrosEmMassa = (): { corpo: ParametrosEmMassaInput } | { erro: string } => {
    const campos: ParametrosEmMassaInput['campos'] = {};
    if (estoqueEdit.unidadeCompra.aplicar) {
      const valor = estoqueEdit.unidadeCompra.valor.trim();
      campos.unidadeCompra = { aplicar: true, valor: valor === '' ? null : valor };
    }
    if (estoqueEdit.fatorConversao.aplicar) {
      const fator = lerFatorDigitado(estoqueEdit.fatorConversao.valor);
      if (fator.tipo === 'vazio') return { erro: 'Informe o fator de conversão.' };
      if (fator.tipo === 'erro') return { erro: fator.mensagem };
      campos.fatorConversao = { aplicar: true, valor: fator.valor };
    }
    if (estoqueEdit.estoquePreferencialId.aplicar) {
      if (estoqueEdit.estoquePreferencialId.valor === '') return { erro: 'Escolha o estoque preferencial.' };
      campos.estoquePreferencialId = { aplicar: true, valor: estoqueEdit.estoquePreferencialId.valor };
    }
    if (estoqueEdit.altoGiroSemDevolucao.aplicar) {
      campos.altoGiroSemDevolucao = { aplicar: true, valor: estoqueEdit.altoGiroSemDevolucao.valor };
    }
    if (Object.keys(campos).length === 0) return { erro: 'Marque "Aplicar" em ao menos um campo de estoque e compras.' };
    return { corpo: { materialIds: Array.from(selectedIds), campos } };
  };

  const revisarEstoqueEmMassa = () => {
    const montado = montarParametrosEmMassa();
    if ('erro' in montado) {
      setEstoqueErro(montado.erro);
      return;
    }
    setEstoqueErro(null);
    setEstoqueConfirmOpen(true);
  };

  const handleExecutarEstoqueEmMassa = async () => {
    const montado = montarParametrosEmMassa();
    if ('erro' in montado) {
      setEstoqueErro(montado.erro);
      return;
    }
    setEstoqueAplicando(true);
    setEstoqueErro(null);
    try {
      const res = await apiMutate('/api/v1/estoque/materiais/parametros/editar-em-massa', {
        method: 'PATCH',
        body: montado.corpo,
      });
      if (!res.ok) {
        const erro = await lerErroApi(res, 'Falha ao aplicar os parâmetros de estoque em massa.');
        setEstoqueErro(mensagemErroParametros(erro.codigo) ?? erro.mensagem);
        return;
      }
      const resultado = (await res.json()) as ParametrosEmMassaResultado;
      setFeedback({
        tipo: 'ok',
        texto: `Estoque e compras atualizado: ${resultado.totalAlterados} alterados, ${resultado.totalInalterados} inalterados.`,
      });
      setEstoqueConfirmOpen(false);
      // WR-C08: nada do que foi aplicado fica marcado para a próxima seleção.
      setEstoqueEdit(ESTOQUE_EDIT_INICIAL);
      setModalOpen(false);
      setSelectedIds(new Set());
      void carregarPagina();
    } catch {
      setEstoqueErro('Não foi possível aplicar os parâmetros de estoque. Verifique a conexão e tente novamente.');
    } finally {
      setEstoqueAplicando(false);
    }
  };

  const handleExecutarEdicao = async () => {
    setApplying(true);
    setFeedback(null);

    const payload: MaterialMassEditPayload = {
      materialIds: Array.from(selectedIds),
      campos: {},
    };

    if (camposEdit.family.aplicar) payload.campos.family = { aplicar: true, valor: camposEdit.family.valor || null };
    if (camposEdit.status.aplicar) payload.campos.status = { aplicar: true, valor: camposEdit.status.valor };
    if (camposEdit.kind.aplicar) payload.campos.kind = { aplicar: true, valor: camposEdit.kind.valor };
    if (camposEdit.stage.aplicar) payload.campos.stage = { aplicar: true, valor: camposEdit.stage.valor || null };
    if (camposEdit.unit.aplicar) payload.campos.unit = { aplicar: true, valor: camposEdit.unit.valor || null };
    if (camposEdit.cellCount.aplicar) payload.campos.cellCount = { aplicar: true, valor: Number(camposEdit.cellCount.valor) };
    if (camposEdit.description.aplicar) payload.campos.description = { aplicar: true, valor: camposEdit.description.valor || null };
    if (camposEdit.pinta.aplicar) payload.campos.pinta = { aplicar: true, valor: camposEdit.pinta.valor };
    if (camposEdit.tipoTinta.aplicar) payload.campos.tipoTinta = { aplicar: true, valor: umDos(camposEdit.tipoTinta.valor, ['PO', 'LIQUIDA', 'PTFE'] satisfies readonly TipoTinta[]) };
    if (camposEdit.corCodigo.aplicar) payload.campos.corCodigo = { aplicar: true, valor: camposEdit.corCodigo.valor || null };
    if (camposEdit.corFamilia.aplicar) payload.campos.corFamilia = { aplicar: true, valor: umDos(camposEdit.corFamilia.valor, ['CLARA', 'ESCURA', 'PRETA', 'CINZA'] satisfies readonly FamiliaCor[]) };
    if (camposEdit.porte.aplicar) payload.campos.porte = { aplicar: true, valor: umDos(camposEdit.porte.valor, ['PEQUENA', 'QUALQUER'] satisfies readonly PortePeca[]) };
    if (camposEdit.exigeIsolamento.aplicar) payload.campos.exigeIsolamento = { aplicar: true, valor: camposEdit.exigeIsolamento.valor === 'true' ? true : (camposEdit.exigeIsolamento.valor === 'false' ? false : null) };
    if (camposEdit.tipoBanho.aplicar) payload.campos.tipoBanho = { aplicar: true, valor: umDos(camposEdit.tipoBanho.valor, ['ACO', 'ALUMINIO', 'JATEAMENTO'] satisfies readonly TipoBanho[]) };
    if (camposEdit.areaUnitariaM2.aplicar) payload.campos.areaUnitariaM2 = { aplicar: true, valor: camposEdit.areaUnitariaM2.valor ? Number(camposEdit.areaUnitariaM2.valor) : null };
    if (camposEdit.pecasPorGancheira.aplicar) payload.campos.pecasPorGancheira = { aplicar: true, valor: camposEdit.pecasPorGancheira.valor ? Number(camposEdit.pecasPorGancheira.valor) : null };

    try {
      const res = await apiMutate('/api/v1/cadastros/materiais/editar-em-massa', {
        method: 'PATCH',
        body: payload,
      });

      if (!res.ok) throw new Error(`Falha ao aplicar edição: HTTP ${res.status}`);
      const json: unknown = await res.json();
      if (!ehResultadoEmMassa(json)) throw new Error('Resposta da edição em massa inválida.');
      const resultado = json;

      setFeedback({
        tipo: 'ok',
        texto: `Edição em massa concluída: ${resultado.totalAlterados} alterados, ${resultado.totalInalterados} inalterados.`,
      });

      setConfirmOpen(false);
      setModalOpen(false);
      setSelectedIds(new Set());
      void carregarPagina();
    } catch (err) {
      setFeedback({
        tipo: 'erro',
        texto: err instanceof Error ? err.message : 'Falha na requisição de edição em massa',
      });
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="flex flex-col h-full space-y-3 p-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b pb-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Package className="w-6 h-6 text-slate-800" />
            Materiais & Edição em Massa
          </h1>
          <p className="text-xs text-slate-500">
            Consulta paginada sobre os materiais cadastrados, com edição em lote auditada.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setMaterialEditando(null)}
            className="flex items-center gap-1 px-3 py-1.5 bg-slate-900 text-white rounded-lg text-xs font-semibold hover:bg-slate-800"
          >
            <Plus className="w-3.5 h-3.5" />
            Novo Material
          </button>
          <button
            onClick={() => void carregarPagina()}
            disabled={loading}
            className="flex items-center gap-1 px-3 py-1.5 border rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Feedback Banner */}
      {feedback && (
        <div
          className={`p-3 rounded-lg text-xs flex items-center gap-2 ${
            feedback.tipo === 'ok'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-rose-50 text-rose-800 border border-rose-200'
          }`}
        >
          {feedback.tipo === 'ok' ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          ) : (
            <AlertOctagon className="w-4 h-4 text-rose-600 shrink-0" />
          )}
          <span>{feedback.texto}</span>
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 bg-slate-50 p-2.5 rounded-lg border text-xs">
        <input type="search" aria-label="Buscar materiais por código ou nome" placeholder="Buscar código ou nome…" value={busca} onChange={e => { setBusca(e.target.value); setPage(1); setPageCursors([null]); }} className="min-w-0 flex-1 rounded-lg border px-3 py-2 text-sm" />
        <div className="flex items-center gap-1">
          <Filter className="w-3.5 h-3.5 text-slate-500" />
          <span className="font-semibold text-slate-700">Filtros:</span>
        </div>

        <input
          type="text"
          placeholder="Família (ex: FAM-01)..."
          aria-label="Filtrar por família"
          value={buscaFamily}
          onChange={(e) => {
            setBuscaFamily(e.target.value);
            setPage(1);
            setPageCursors([null]);
          }}
          className="px-2.5 py-1 bg-white border rounded text-xs text-slate-800"
        />

        <CampoSelect
          aria-label="Filtrar por status"
          size="sm"
          value={buscaStatus}
          onValueChange={(valor) => {
            setBuscaStatus(valor);
            setPage(1);
            setPageCursors([null]);
          }}
          options={[
            { value: '', label: 'Todos os status' },
            { value: 'ACTIVE', label: 'Ativo' },
            { value: 'INACTIVE', label: 'Inativo' },
            { value: 'DRAFT', label: 'Rascunho' },
          ]}
          className="w-44 text-xs text-slate-800"
        />

        <CampoSelect
          aria-label="Filtrar por tipo"
          size="sm"
          value={buscaKind}
          onValueChange={(valor) => {
            setBuscaKind(valor);
            setPage(1);
            setPageCursors([null]);
          }}
          options={[
            { value: '', label: 'Todos os Tipos' },
            { value: 'PRODUTO', label: 'Produto' },
            { value: 'INSUMO', label: 'Insumo' },
            { value: 'SERVICO', label: 'Serviço' },
          ]}
          className="w-44 text-xs text-slate-800"
        />

        <span className="ml-auto font-mono text-slate-500">
          {total} {total === 1 ? 'material encontrado' : 'materiais encontrados'}
        </span>
      </div>

      {/* Grade paginada no servidor */}
      <div className="flex-1 min-h-[400px] border rounded-lg overflow-hidden bg-white shadow-inner relative">
        <div className="max-h-[580px] overflow-auto">
        {loading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 10 }, (_, i) => (
              <div key={i} className="h-8 bg-slate-100 animate-pulse rounded" />
            ))}
          </div>
        ) : materiais.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-xs">
            Nenhum material encontrado para os filtros aplicados.
          </div>
        ) : (
          <table className="w-full text-left text-xs border-collapse">
            <thead className="sticky top-0 bg-slate-100 z-10 border-b shadow-xs">
              <tr className="text-slate-600 font-semibold uppercase tracking-wider text-[11px]">
                <th className="py-2.5 px-3 w-10 text-center">
                  <button type="button" aria-label="Selecionar todos os materiais desta página" aria-pressed={materiais.length > 0 && materiais.every(m => selectedIds.has(m.id))} onClick={toggleSelectAll} className="inline-flex min-h-9 min-w-9 items-center justify-center text-slate-600 hover:text-slate-900">
                    {selectedIds.size > 0 && selectedIds.size === materiais.length ? (
                      <CheckSquare className="w-4 h-4 text-slate-900" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                  </button>
                </th>
                <th className="py-2.5 px-3">Código</th>
                <th className="py-2.5 px-3">Nome / Descrição</th>
                <th className="py-2.5 px-3">Família</th>
                <th className="py-2.5 px-3">Tipo</th>
                <th className="py-2.5 px-3 text-center">Banho</th>
                <th className="py-2.5 px-3 text-center">Tinta</th>
                <th className="py-2.5 px-3 text-center">Cor</th>
                <th className="py-2.5 px-3 text-center">Unidade</th>
                <th className="py-2.5 px-3 text-center">Status</th>
                <th className="py-2.5 px-3 text-center w-10">Editar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-normal">
              {materiais.map((mat) => {
                const isSelected = selectedIds.has(mat.id);
                return (
                  <tr
                    key={mat.id}
                    onClick={() => toggleSelect(mat.id)}
                    className={`cursor-pointer transition-colors ${
                      isSelected ? 'bg-slate-100 font-medium' : 'hover:bg-slate-50'
                    }`}
                  >
                    <td className="py-2 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <button type="button" aria-label={'Selecionar material ' + mat.codigo} aria-pressed={isSelected} onClick={() => toggleSelect(mat.id)} className="inline-flex min-h-9 min-w-9 items-center justify-center text-slate-600">
                        {isSelected ? (
                          <CheckSquare className="w-4 h-4 text-slate-900" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                    <td className="py-2 px-3 font-mono font-medium text-slate-900">{mat.codigo}</td>
                    <td className="py-2 px-3 text-slate-800">
                      <div className="flex min-w-64 flex-col items-start gap-2">
                        {mat.nome}
                        {mat.atributosIncompletos && (
                          <button type="button" onClick={(evento) => { evento.stopPropagation(); setSelectedIds(new Set([mat.id])); }} className="flex min-h-11 items-center gap-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-left text-xs text-amber-800">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            Falta informar: {camposProcessoPendentes({ ...mat, pinta: mat.pinta ?? true }).join(', ')}. Selecionar este material para corrigir em Editar Selecionados.
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-slate-600">{mat.family ?? '—'}</td>
                    <td className="py-2 px-3 text-slate-600">{rotuloKindMaterial(mat.kind)}</td>
                    <td className="py-2 px-3 text-center">
                      {mat.tipoBanho ? <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold border border-slate-300 bg-white text-slate-700">{rotuloBanho(umDos(String(mat.tipoBanho), ['ACO', 'ALUMINIO', 'JATEAMENTO'] satisfies readonly TipoBanho[]))}</span> : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-2 px-3 text-center">
                      {mat.tipoTinta ? <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold border border-slate-300 bg-white text-slate-700">{rotuloTinta(umDos(String(mat.tipoTinta), ['PO', 'LIQUIDA', 'PTFE'] satisfies readonly TipoTinta[]))}</span> : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-2 px-3 text-center text-slate-600 text-[10px]">
                      {mat.corCodigo ? `${mat.corCodigo} (${mat.corFamilia?.charAt(0) ?? ''})` : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-2 px-3 text-center font-mono text-slate-600">{mat.unit ?? 'un'}</td>
                    <td className="py-2 px-3 text-center">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          mat.status === 'ACTIVE'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-slate-100 text-slate-600 border'
                        }`}
                      >
                        {rotuloStatus(mat.status)}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => setMaterialEditando(mat)}
                        className="px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 rounded"
                      >
                        Editar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        </div>
        <TablePagination
          page={page}
          pageSize={pageSize}
          totalItems={total}
          itemLabel="materiais"
          canGoNext={hasMore}
          onPageChange={(nextPage) => {
            if (nextPage < page || (nextPage === page + 1 && nextCursor)) setPage(nextPage);
          }}
          onPageSizeChange={(nextPageSize) => {
            setPageSize(nextPageSize);
            setPage(1);
            setPageCursors([null]);
          }}
        />
      </div>

      {/* Barra de Ação Flutuante (03-UI-SPEC §C) */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-4 z-40 animate-in fade-in slide-in-from-bottom-3 duration-200">
          <span className="text-xs font-semibold">
            {selectedIds.size} {selectedIds.size === 1 ? 'material selecionado' : 'materiais selecionados'}
          </span>

          <div className="h-4 w-px bg-slate-700" />

          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-slate-900 rounded-md text-xs font-semibold hover:bg-slate-100"
          >
            <Edit3 className="w-3.5 h-3.5" />
            Editar Selecionados
          </button>

          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-xs text-slate-300 hover:text-white"
          >
            Limpar Seleção
          </button>
        </div>
      )}

      {/* Modal de Configuração da Edição em Massa */}
      {modalOpen && (
        <FormDialog open={modalOpen} onOpenChange={setModalOpen}>
          <FormDialogContent aria-describedby={undefined}>
            <FormDialogHeader>
              <div>
                <FormDialogTitle>
                  Edição em Massa ({selectedIds.size} materiais)
                </FormDialogTitle>
                <p className="text-xs text-slate-500">
                  Marque "Aplicar" apenas nos campos que deseja sobrescrever em lote.
                </p>
              </div>

            </FormDialogHeader>

            <FormDialogBody className="space-y-3">
              {/* Campo Família */}
              <div className="flex items-center justify-between p-2.5 border rounded-md bg-slate-50/50">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer" htmlFor="field-materiais-mass-edit-page-27312">
                  <input
                    type="checkbox"
                    checked={camposEdit.family.aplicar}
                    onChange={(e) =>
                      setCamposEdit((prev) => ({
                        ...prev,
                        family: { ...prev.family, aplicar: e.target.checked },
                      }))
                    }
                  />
                  <span>Aplicar Família:</span>
                </label>
                <input id="field-materiais-mass-edit-page-27312"
                  type="text"
                  placeholder="Ex: FAM-ZINCO"
                  disabled={!camposEdit.family.aplicar}
                  value={camposEdit.family.valor}
                  onChange={(e) =>
                    setCamposEdit((prev) => ({
                      ...prev,
                      family: { ...prev.family, valor: e.target.value },
                    }))
                  }
                  className="w-48 px-2.5 py-1 text-xs border rounded bg-white disabled:bg-slate-100"
                />
              </div>

              {/* Campo Status */}
              <div className="flex items-center justify-between p-2.5 border rounded-md bg-slate-50/50">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={camposEdit.status.aplicar}
                    onChange={(e) =>
                      setCamposEdit((prev) => ({
                        ...prev,
                        status: { ...prev.status, aplicar: e.target.checked },
                      }))
                    }
                  />
                  <span>Aplicar Status:</span>
                </label>
                <CampoSelect
                  aria-label="Status a aplicar"
                  size="sm"
                  disabled={!camposEdit.status.aplicar}
                  value={camposEdit.status.valor}
                  onValueChange={(valor) =>
                    setCamposEdit((prev) => ({
                      ...prev,
                      status: { ...prev.status, valor },
                    }))
                  }
                  options={[
                    { value: 'ACTIVE', label: 'Ativo' },
                    { value: 'INACTIVE', label: 'Inativo' },
                    { value: 'DRAFT', label: 'Rascunho' },
                  ]}
                  className="w-48 text-xs"
                />
              </div>

              {/* Campo Tipo (Kind) */}
              <div className="flex items-center justify-between p-2.5 border rounded-md bg-slate-50/50">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={camposEdit.kind.aplicar}
                    onChange={(e) =>
                      setCamposEdit((prev) => ({
                        ...prev,
                        kind: { ...prev.kind, aplicar: e.target.checked },
                      }))
                    }
                  />
                  <span>Aplicar Tipo:</span>
                </label>
                <CampoSelect
                  aria-label="Tipo a aplicar"
                  size="sm"
                  disabled={!camposEdit.kind.aplicar}
                  value={camposEdit.kind.valor}
                  onValueChange={(valor) =>
                    setCamposEdit((prev) => ({
                      ...prev,
                      kind: { ...prev.kind, valor },
                    }))
                  }
                  options={[
                    { value: 'PRODUTO', label: 'Produto' },
                    { value: 'INSUMO', label: 'Insumo' },
                    { value: 'SERVICO', label: 'Serviço' },
                  ]}
                  className="w-48 text-xs"
                />
              </div>

              {/* Campo Unidade */}
              <div className="flex items-center justify-between p-2.5 border rounded-md bg-slate-50/50">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer" htmlFor="field-materiais-mass-edit-page-31501">
                  <input
                    type="checkbox"
                    checked={camposEdit.unit.aplicar}
                    onChange={(e) =>
                      setCamposEdit((prev) => ({
                        ...prev,
                        unit: { ...prev.unit, aplicar: e.target.checked },
                      }))
                    }
                  />
                  <span>Aplicar Unidade de estoque / consumo:</span>
                </label>
                <input id="field-materiais-mass-edit-page-31501"
                  type="text"
                  placeholder="Ex: un, kg, m²"
                  disabled={!camposEdit.unit.aplicar}
                  value={camposEdit.unit.valor}
                  onChange={(e) =>
                    setCamposEdit((prev) => ({
                      ...prev,
                      unit: { ...prev.unit, valor: e.target.value },
                    }))
                  }
                  className="w-48 px-2.5 py-1 text-xs border rounded bg-white disabled:bg-slate-100"
                />
              </div>


              {/* Process Attributes */}
              <div className="flex items-center justify-between p-2.5 border rounded-md bg-slate-50/50">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={camposEdit.pinta.aplicar} onChange={(e) => setCamposEdit((prev) => ({ ...prev, pinta: { ...prev.pinta, aplicar: e.target.checked } }))} />
                  <span>Aplicar Pinta:</span>
                </label>
                <div className="w-48 flex items-center">
                  <input type="checkbox" disabled={!camposEdit.pinta.aplicar} checked={camposEdit.pinta.valor} onChange={(e) => setCamposEdit((prev) => ({ ...prev, pinta: { ...prev.pinta, valor: e.target.checked } }))} className="mr-2" />
                  <span className="text-xs text-slate-600">Sim</span>
                </div>
              </div>

              <div className="flex items-center justify-between p-2.5 border rounded-md bg-slate-50/50">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={camposEdit.tipoTinta.aplicar} onChange={(e) => setCamposEdit((prev) => ({ ...prev, tipoTinta: { ...prev.tipoTinta, aplicar: e.target.checked } }))} />
                  <span>Aplicar Tipo de Tinta:</span>
                </label>
                <CampoSelect aria-label="Tipo de tinta a aplicar" size="sm" disabled={!camposEdit.tipoTinta.aplicar} value={camposEdit.tipoTinta.valor} onValueChange={(valor) => setCamposEdit((prev) => ({ ...prev, tipoTinta: { ...prev.tipoTinta, valor } }))}
                  options={[{ value: '', label: '(Limpar)' }, ...opcoesTinta.map((o) => ({ value: o.value, label: o.label }))]} className="w-48 text-xs" />
              </div>

              <div className="flex items-center justify-between p-2.5 border rounded-md bg-slate-50/50">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer" htmlFor="field-materiais-mass-edit-page-34738">
                  <input type="checkbox" checked={camposEdit.corCodigo.aplicar} onChange={(e) => setCamposEdit((prev) => ({ ...prev, corCodigo: { ...prev.corCodigo, aplicar: e.target.checked } }))} />
                  <span>Aplicar Código da Cor:</span>
                </label>
                <input id="field-materiais-mass-edit-page-34738" type="text" placeholder="Ex: RAL9010" disabled={!camposEdit.corCodigo.aplicar} value={camposEdit.corCodigo.valor} onChange={(e) => setCamposEdit((prev) => ({ ...prev, corCodigo: { ...prev.corCodigo, valor: e.target.value } }))} className="w-48 px-2.5 py-1 text-xs border rounded bg-white disabled:bg-slate-100" />
              </div>

              <div className="flex items-center justify-between p-2.5 border rounded-md bg-slate-50/50">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={camposEdit.corFamilia.aplicar} onChange={(e) => setCamposEdit((prev) => ({ ...prev, corFamilia: { ...prev.corFamilia, aplicar: e.target.checked } }))} />
                  <span>Aplicar Família de Cor:</span>
                </label>
                <CampoSelect aria-label="Família de cor a aplicar" size="sm" disabled={!camposEdit.corFamilia.aplicar} value={camposEdit.corFamilia.valor} onValueChange={(valor) => setCamposEdit((prev) => ({ ...prev, corFamilia: { ...prev.corFamilia, valor } }))}
                  options={[{ value: '', label: '(Limpar)' }, ...opcoesFamiliaCor.map((o) => ({ value: o.value, label: o.label }))]} className="w-48 text-xs" />
              </div>

              <div className="flex items-center justify-between p-2.5 border rounded-md bg-slate-50/50">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={camposEdit.porte.aplicar} onChange={(e) => setCamposEdit((prev) => ({ ...prev, porte: { ...prev.porte, aplicar: e.target.checked } }))} />
                  <span>Aplicar Porte:</span>
                </label>
                <CampoSelect aria-label="Porte a aplicar" size="sm" disabled={!camposEdit.porte.aplicar} value={camposEdit.porte.valor} onValueChange={(valor) => setCamposEdit((prev) => ({ ...prev, porte: { ...prev.porte, valor } }))}
                  options={[{ value: '', label: '(Limpar)' }, ...opcoesPorte.map((o) => ({ value: o.value, label: o.label }))]} className="w-48 text-xs" />
              </div>

              <div className="flex items-center justify-between p-2.5 border rounded-md bg-slate-50/50">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={camposEdit.exigeIsolamento.aplicar} onChange={(e) => setCamposEdit((prev) => ({ ...prev, exigeIsolamento: { ...prev.exigeIsolamento, aplicar: e.target.checked } }))} />
                  <span>Aplicar Isolamento:</span>
                </label>
                <CampoSelect aria-label="Isolamento a aplicar" size="sm" disabled={!camposEdit.exigeIsolamento.aplicar} value={camposEdit.exigeIsolamento.valor} onValueChange={(valor) => setCamposEdit((prev) => ({ ...prev, exigeIsolamento: { ...prev.exigeIsolamento, valor } }))}
                  options={[{ value: '', label: '(Não informado)' }, { value: 'true', label: 'Sim' }, { value: 'false', label: 'Não' }]} className="w-48 text-xs" />
              </div>

              <div className="flex items-center justify-between p-2.5 border rounded-md bg-slate-50/50">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={camposEdit.tipoBanho.aplicar} onChange={(e) => setCamposEdit((prev) => ({ ...prev, tipoBanho: { ...prev.tipoBanho, aplicar: e.target.checked } }))} />
                  <span>Aplicar Tipo de Banho:</span>
                </label>
                <CampoSelect aria-label="Tipo de banho a aplicar" size="sm" disabled={!camposEdit.tipoBanho.aplicar} value={camposEdit.tipoBanho.valor} onValueChange={(valor) => setCamposEdit((prev) => ({ ...prev, tipoBanho: { ...prev.tipoBanho, valor } }))}
                  options={[{ value: '', label: '(Limpar)' }, ...opcoesBanho.map((o) => ({ value: o.value, label: o.label }))]} className="w-48 text-xs" />
              </div>

              <div className="flex items-center justify-between p-2.5 border rounded-md bg-slate-50/50">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer" htmlFor="field-materiais-mass-edit-page-39656">
                  <input type="checkbox" checked={camposEdit.areaUnitariaM2.aplicar} onChange={(e) => setCamposEdit((prev) => ({ ...prev, areaUnitariaM2: { ...prev.areaUnitariaM2, aplicar: e.target.checked } }))} />
                  <span>Aplicar Área Unitária (m²):</span>
                </label>
                <input id="field-materiais-mass-edit-page-39656" type="number" step="0.01" min="0" placeholder="Ex: 0.25" disabled={!camposEdit.areaUnitariaM2.aplicar} value={camposEdit.areaUnitariaM2.valor} onChange={(e) => setCamposEdit((prev) => ({ ...prev, areaUnitariaM2: { ...prev.areaUnitariaM2, valor: e.target.value } }))} className="w-48 px-2.5 py-1 text-xs border rounded bg-white disabled:bg-slate-100 font-mono" />
              </div>

              <div className="flex items-center justify-between p-2.5 border rounded-md bg-slate-50/50">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer" htmlFor="field-materiais-mass-edit-page-40589">
                  <input type="checkbox" checked={camposEdit.pecasPorGancheira.aplicar} onChange={(e) => setCamposEdit((prev) => ({ ...prev, pecasPorGancheira: { ...prev.pecasPorGancheira, aplicar: e.target.checked } }))} />
                  <span>Aplicar Peças/Gancheira:</span>
                </label>
                <input id="field-materiais-mass-edit-page-40589" type="number" min="1" placeholder="Ex: 20" disabled={!camposEdit.pecasPorGancheira.aplicar} value={camposEdit.pecasPorGancheira.valor} onChange={(e) => setCamposEdit((prev) => ({ ...prev, pecasPorGancheira: { ...prev.pecasPorGancheira, valor: e.target.value } }))} className="w-48 px-2.5 py-1 text-xs border rounded bg-white disabled:bg-slate-100 font-mono" />
              </div>

              {/* Estoque e compras (Fase 13): aplicar + valor, com botão e confirmação próprios */}
              {podeConfigurarEstoque && (
                <fieldset className="space-y-3 pt-3 border-t">
                  <legend className="text-xs font-semibold text-slate-900 pr-2">Estoque e compras</legend>

                  <div className="flex items-center justify-between p-2.5 border rounded-md bg-slate-50/50">
                    <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer" htmlFor="field-materiais-mass-edit-page-41853">
                      <input
                        type="checkbox"
                        checked={estoqueEdit.unidadeCompra.aplicar}
                        onChange={(e) => setEstoqueEdit((prev) => ({ ...prev, unidadeCompra: { ...prev.unidadeCompra, aplicar: e.target.checked } }))}
                      />
                      <span>Aplicar Unidade de compra:</span>
                    </label>
                    <input id="field-materiais-mass-edit-page-41853"
                      type="text"
                      placeholder="Ex: caixa, lata"
                      maxLength={20}
                      disabled={!estoqueEdit.unidadeCompra.aplicar}
                      value={estoqueEdit.unidadeCompra.valor}
                      onChange={(e) => setEstoqueEdit((prev) => ({ ...prev, unidadeCompra: { ...prev.unidadeCompra, valor: e.target.value } }))}
                      className="w-48 px-2.5 py-1 text-xs border rounded bg-white disabled:bg-slate-100"
                    />
                  </div>

                  <div className="flex items-center justify-between p-2.5 border rounded-md bg-slate-50/50">
                    <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer" htmlFor="field-materiais-mass-edit-page-43081">
                      <input
                        type="checkbox"
                        checked={estoqueEdit.fatorConversao.aplicar}
                        onChange={(e) => setEstoqueEdit((prev) => ({ ...prev, fatorConversao: { ...prev.fatorConversao, aplicar: e.target.checked } }))}
                      />
                      <span>Aplicar Fator de conversão:</span>
                    </label>
                    <input id="field-materiais-mass-edit-page-43081"
                      type="text"
                      inputMode="decimal"
                      placeholder="Ex: 20"
                      disabled={!estoqueEdit.fatorConversao.aplicar}
                      value={estoqueEdit.fatorConversao.valor}
                      onChange={(e) => setEstoqueEdit((prev) => ({ ...prev, fatorConversao: { ...prev.fatorConversao, valor: e.target.value } }))}
                      className="w-48 px-2.5 py-1 text-xs border rounded bg-white disabled:bg-slate-100 font-mono"
                    />
                  </div>

                  <div className="flex items-center justify-between p-2.5 border rounded-md bg-slate-50/50">
                    <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={estoqueEdit.estoquePreferencialId.aplicar}
                        onChange={(e) =>
                          setEstoqueEdit((prev) => ({ ...prev, estoquePreferencialId: { ...prev.estoquePreferencialId, aplicar: e.target.checked } }))
                        }
                      />
                      <span>Aplicar Estoque preferencial:</span>
                    </label>
                    <div className="w-48">
                      <LocalizacaoSelect
                        value={estoqueEdit.estoquePreferencialId.valor}
                        onChange={(id) => setEstoqueEdit((prev) => ({ ...prev, estoquePreferencialId: { ...prev.estoquePreferencialId, valor: id } }))}
                        rotulo="Estoque preferencial"
                        aoCarregar={(locais) => setNomesLocaisEstoque(Object.fromEntries(locais.map((local) => [local.id, local.nome])))}
                        desabilitada={!estoqueEdit.estoquePreferencialId.aplicar}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-2.5 border rounded-md bg-slate-50/50">
                    <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={estoqueEdit.altoGiroSemDevolucao.aplicar}
                        onChange={(e) =>
                          setEstoqueEdit((prev) => ({ ...prev, altoGiroSemDevolucao: { ...prev.altoGiroSemDevolucao, aplicar: e.target.checked } }))
                        }
                      />
                      <span>Aplicar Alto giro, sem devolução:</span>
                    </label>
                    <div className="w-48 flex items-center">
                      <input
                        type="checkbox"
                        disabled={!estoqueEdit.altoGiroSemDevolucao.aplicar}
                        checked={estoqueEdit.altoGiroSemDevolucao.valor}
                        onChange={(e) =>
                          setEstoqueEdit((prev) => ({ ...prev, altoGiroSemDevolucao: { ...prev.altoGiroSemDevolucao, valor: e.target.checked } }))
                        }
                        className="mr-2"
                      />
                      <span className="text-xs text-slate-600">Sim</span>
                    </div>
                  </div>

                  {estoqueErro && !estoqueConfirmOpen && (
                    <p role="alert" className="text-xs text-rose-700">
                      {estoqueErro}
                    </p>
                  )}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={revisarEstoqueEmMassa}
                      className="px-3 py-1.5 border rounded-md text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Revisar & aplicar estoque e compras
                    </button>
                  </div>
                </fieldset>
              )}

              {/* Campo Células (CellCount) */}
              <div className="flex items-center justify-between p-2.5 border rounded-md bg-slate-50/50">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer" htmlFor="field-materiais-mass-edit-page-47873">
                  <input
                    type="checkbox"
                    checked={camposEdit.cellCount.aplicar}
                    onChange={(e) =>
                      setCamposEdit((prev) => ({
                        ...prev,
                        cellCount: { ...prev.cellCount, aplicar: e.target.checked },
                      }))
                    }
                  />
                  <span>Aplicar Qtd Células:</span>
                </label>
                <input id="field-materiais-mass-edit-page-47873"
                  type="number"
                  min="1"
                  disabled={!camposEdit.cellCount.aplicar}
                  value={camposEdit.cellCount.valor}
                  onChange={(e) =>
                    setCamposEdit((prev) => ({
                      ...prev,
                      cellCount: { ...prev.cellCount, valor: Number(e.target.value) },
                    }))
                  }
                  className="w-48 px-2.5 py-1 text-xs border rounded bg-white disabled:bg-slate-100 font-mono"
                />
              </div>
            </FormDialogBody>

            <FormDialogFooter>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 border rounded-md text-xs font-semibold text-slate-700 hover:bg-white"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                className="px-4 py-2 bg-slate-900 text-white rounded-md text-xs font-semibold hover:bg-slate-800"
              >
                Revisar & Aplicar
              </button>
            </FormDialogFooter>
          </FormDialogContent>
        </FormDialog>
      )}

      {/* Confirmação Não-Destrutiva */}
      {confirmOpen && (
        <FormDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <FormDialogContent aria-describedby={undefined}>
            <FormDialogHeader>
              <AlertOctagon className="w-6 h-6 text-amber-500 shrink-0" />
              <FormDialogTitle>Confirmar Edição em Massa</FormDialogTitle>
            </FormDialogHeader>
            <FormDialogBody><p className="text-xs text-slate-600 leading-relaxed">
              Você está prestes a alterar <strong className="text-slate-900">{selectedIds.size}</strong> materiais.
              Cada alteração será registrada individualmente na trilha de auditoria (AuditEvent).
            </p>
            </FormDialogBody><FormDialogFooter>
              <button
                onClick={() => setConfirmOpen(false)}
                className="px-3 py-1.5 border rounded-md text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Voltar
              </button>
              <button
                onClick={() => void handleExecutarEdicao()}
                disabled={applying}
                className="px-4 py-1.5 bg-slate-900 text-white rounded-md text-xs font-semibold hover:bg-slate-800 disabled:opacity-50"
              >
                {applying ? 'Aplicando alterações...' : 'Confirmar e Aplicar'}
              </button>
            </FormDialogFooter>
          </FormDialogContent>
        </FormDialog>
      )}

      {/* Confirmação da edição em massa de Estoque e compras (Fase 13) */}
      {estoqueConfirmOpen && (
        <FormDialog open={estoqueConfirmOpen} onOpenChange={setEstoqueConfirmOpen}>
          <FormDialogContent aria-describedby={undefined}>
            <FormDialogHeader>
              <AlertOctagon className="w-6 h-6 text-amber-500 shrink-0" />
              <FormDialogTitle>Confirmar Estoque e compras em massa</FormDialogTitle>
            </FormDialogHeader>
            <FormDialogBody className="space-y-4">
            <p className="text-xs text-slate-600 leading-relaxed">
              Altera <strong className="text-slate-900">{selectedIds.size}</strong> materiais. Fator de conversão em massa só deve ser aplicado a materiais da mesma embalagem.
            </p>
            <ul className="text-xs text-slate-800 list-disc pl-5 space-y-0.5" aria-label="Alterações que serão aplicadas">
              {(() => {
                const montado = montarParametrosEmMassa();
                return 'corpo' in montado ? linhasResumoEmMassa(montado.corpo.campos, (id) => nomesLocaisEstoque[id] ?? null) : [];
              })().map((linha) => (
                <li key={linha}>{linha}</li>
              ))}
            </ul>
            {estoqueErro && (
              <p role="alert" className="text-xs text-rose-700">
                {estoqueErro}
              </p>
            )}
            </FormDialogBody>
            <FormDialogFooter>
              <button
                type="button"
                onClick={() => setEstoqueConfirmOpen(false)}
                disabled={estoqueAplicando}
                className="px-3 py-1.5 border rounded-md text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={() => void handleExecutarEstoqueEmMassa()}
                disabled={estoqueAplicando}
                className="px-4 py-1.5 bg-slate-900 text-white rounded-md text-xs font-semibold hover:bg-slate-800 disabled:opacity-50"
              >
                {estoqueAplicando ? 'Aplicando alterações...' : 'Confirmar e Aplicar'}
              </button>
            </FormDialogFooter>
          </FormDialogContent>
        </FormDialog>
      )}

      {/* Modal de Criação/Edição Individual de Material (WR-08) */}
      {materialEditando !== undefined && (
        <MaterialFormModal
          material={materialEditando}
          onClose={() => setMaterialEditando(undefined)}
          onSuccess={() => {
            setMaterialEditando(undefined);
            void carregarPagina();
          }}
        />
      )}
    </div>
  );
};
